import { useState, useEffect, useCallback } from "react";
import "./css/productdetail.css";
import ProductModDetail from "./ProductModDetail";
import { apiGet, apiDelete } from "../../../utils/apiClient";
import { formatDateTime } from "../../../utils/commonService";

const ProductDetail = ({ productCode, onClose, onRefresh }) => {
  const [isModOpen, setIsModOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [product, setProduct] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const loadProduct = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiGet(`/store/product/detail/${productCode}`);

      if (response.ok) {
        const data = await response.json();
        setProduct(data);
        setSelectedImageIndex(0);
      } else {
        setError("상품 정보를 불러오는데 실패했습니다.");
      }
    } catch (err) {
      setError("상품 정보를 불러오는데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  }, [productCode]);

  useEffect(() => {
    loadProduct();
  }, [loadProduct, refreshKey]);

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
  };

  // 삭제 처리
  const handleDelete = async () => {
    if (!window.confirm("정말로 이 상품을 삭제하시겠습니까?")) {
      return;
    }

    try {
      const response = await apiDelete(`/store/product/${productCode}/delete`);

      if (response.ok) {
        alert("상품이 삭제되었습니다.");
        if (onRefresh) {
          onRefresh(); // 목록 새로고침
        }
        onClose(); // 모달 닫기
      } else {
        const errorData = await response.json();
        alert(errorData.message || "상품 삭제에 실패했습니다.");
      }
    } catch (err) {
      alert("상품 삭제 중 오류가 발생했습니다.");
    }
  };

  if (loading) return <div>불러오는 중...</div>;
  if (error) return <div>{error}</div>;
  if (!product) return null;

  return (
    <>
      {!isModOpen && (
        <div className="productdetail-wrap" key={refreshKey}>
          <div className="productdetail-modal">
            <div className="productdetail-header">상품 상세 정보</div>

            <div className="productdetail-top-content">
              <div className="productdetail-gallery-container">
                {/* 메인 이미지 */}
                <div className="productdetail-main-image-wrapper">
                  {product.imgs?.files?.length > 0 ? (
                    <img
                      src={product.imgs.files[selectedImageIndex].fileUrl}
                      alt={product.productNm}
                      className="productdetail-main-image"
                    />
                  ) : (
                    <div className="productdetail-image-placeholder">이미지 없음</div>
                  )}
                </div>

                {/* 서브 이미지 썸네일 갤러리 */}
                {product.imgs?.files?.length > 0 && (
                  <div className="productdetail-thumbnail-container">
                    {/* 왼쪽 화살표 */}
                    <button
                      type="button"
                      onClick={() => setSelectedImageIndex(prev => prev > 0 ? prev - 1 : product.imgs.files.length - 1)}
                      className="productdetail-arrow-btn"
                    >
                      ◀
                    </button>

                    {/* 썸네일 이미지들 - 고정 영역 (항상 3개 박스 표시) */}
                    <div className="productdetail-thumbnail-list">
                      {[0, 1, 2].map((slotIndex) => (
                        <div
                          key={slotIndex}
                          className={`productdetail-thumbnail-slot ${
                            product.imgs.files[slotIndex] && selectedImageIndex === slotIndex ? 'active' : ''
                          } ${
                            product.imgs.files[slotIndex] ? 'has-image' : 'empty'
                          }`}
                          onClick={() => product.imgs.files[slotIndex] && setSelectedImageIndex(slotIndex)}
                        >
                          {product.imgs.files[slotIndex] ? (
                            <img
                              src={product.imgs.files[slotIndex].fileUrl}
                              alt={`썸네일 ${slotIndex + 1}`}
                              className="productdetail-thumbnail-image"
                            />
                          ) : null}
                        </div>
                      ))}
                    </div>

                    {/* 오른쪽 화살표 */}
                    <button
                      type="button"
                      onClick={() => setSelectedImageIndex(prev => prev < product.imgs.files.length - 1 ? prev + 1 : 0)}
                      className="productdetail-arrow-btn"
                    >
                      ▶
                    </button>
                  </div>
                )}
              </div>
              <div className="productdetail-top-content-info">
                <div className="productdetail-top-content-info1">
                  <div>{product.productNm}</div>
                  <div className="productdetail-top-content-tag">
                    {product.productState}
                  </div>
                </div>

                <div className="productdetail-top-content-info2">
                  상품코드: {product.productCode}
                </div>

                <div className="productdetail-top-content-info3">
                  카테고리: {product.categoryNm}
                </div>

                <div className="productdetail-top-content-info4">
                  판매가격: {product.productPrice}
                </div>

                <div className="productdetail-top-content-info5">
                  재고: {product.productAmount}
                </div>
              </div>
            </div>

            <div className="productdetail-middle-content">
              <div className="productdetail-middle-content-info1">
                <div className="productdetail-middle-content-info-title">
                  할인 여부
                </div>
                <div className="productdetail-middle-content-info-detail">
                  {product.isSale ? "예" : "아니오"}
                </div>
              </div>

              <div className="productdetail-middle-content-info2">
                <div className="productdetail-middle-content-info-title">
                  네고 여부
                </div>
                <div className="productdetail-middle-content-info-detail">
                  {product.isNego ? "예" : "아니오"}
                </div>
              </div>

              {/* <div className="productdetail-middle-content-info3">
                <div className="productdetail-middle-content-info-title">
                  상품구분
                </div>
                <div className="productdetail-middle-content-info-detail">
                  {product.isAdult ? "성인" : "일반"}
                </div>
              </div> */}

              <div className="productdetail-middle-content-info4">
                <div className="productdetail-middle-content-info-title">
                  상품 구분
                </div>
                <div className="productdetail-middle-content-info-detail">
                  {product.isStock ? "서비스" : "일반"}
                </div>
              </div>
            </div>

            <div className="productdetail-description">
              <div className="productdetail-description-title">
                상품 상세설명
              </div>
              <div
                className="productdetail-description-content"
                dangerouslySetInnerHTML={{ __html: product.productDetailDescription || "" }}
              />
            </div>

            <div className="productdetail-bottom-content">
              <div className="productdetail-bottom-content-title">관리정보</div>

              <div className="productdetail-bottom-content-info">
                <div className="productdetail-bottom-content-writer">
                  <div>등록자</div>
                  <div>{product.regNm}</div>
                  <div>등록일시</div>
                  <div>{formatDateTime(product.regDateTime)}</div>
                </div>

                <div className="productdetail-bottom-content-editor">
                  <div>수정자</div>
                  <div>{product.modNm}</div>
                  <div>수정일시</div>
                  <div>{formatDateTime(product.modDateTime)}</div>
                </div>
              </div>
            </div>

            <div className="productdetail-btn-area">
              <button
                className="productdetail-btn productdetail-btn-edit"
                onClick={() => setIsModOpen(true)}
                disabled={product.productState === "판매 제재"}
                style={
                  product.productState === "판매 제재"
                    ? { opacity: 0.5, cursor: "not-allowed" }
                    : {}
                }
              >
                수정
              </button>

              <span className="productdetail-delete-wrapper">
                <button
                  className="productdetail-btn productdetail-btn-delete"
                  onClick={handleDelete}
                  disabled={product.isSale || product.isNego}
                >
                  삭제
                </button>
                {(product.isSale || product.isNego) && (
                  <span className="productdetail-delete-tooltip">
                    할인 또는 네고 진행 중인 상품은 삭제할 수 없습니다.
                  </span>
                )}
              </span>

              <button
                className="productdetail-btn productdetail-btn-close"
                onClick={onClose}
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {isModOpen && (
        <ProductModDetail
          product={product}
          onClose={() => setIsModOpen(false)}
          onSave={() => {
            handleRefresh();
            setIsModOpen(false);
            if (onRefresh) {
              onRefresh(); // 목록도 새로고침
            }
          }}
        />
      )}
    </>
  );
};

export default ProductDetail;
